import dayjs from "dayjs";
import { getFilenameDetails } from "./documents";
import { tamTamyLanguagesMap } from "./lang";
import { computePartial, parseSubmission } from "./partial";

export const QUESTION_MAPPING = {
  firstName: {
    section: 0,
    question: 0,
    answerType: "answerValue",
    behaviour: "value-only",
    box: 0,
    data: (request) => request.creator.firstName,
  },
  lastName: {
    section: 0,
    question: 1,
    answerType: "answerValue",
    behaviour: "value-only",
    box: 0,
    data: (request) => request.creator.lastName,
  },
  birthPlace: {
    section: 0,
    question: 2,
    answerType: "answerValue",
    box: 0,
    data: (request) => request.birthPlace,
  },
  birthdate: {
    section: 0,
    question: 3,
    answerType: "date",
    box: 0,
    data: (request, lang) => dayjs(Number(request.dateOfBirth)).locale(lang).format("L"),
  },
  fiscalCode: {
    section: 0,
    question: 4,
    answerType: "answerValue",
    box: 0,
    data: (request) => request.taxCode,
  },
  gender: {
    section: 0,
    question: 5,
    answerType: "label",
    box: 0,
    data: (request, lang, survey) =>
      survey.definition.sections[0].questions[5].answers[request.gender === "MALE" ? 0 : 1]
        ?.translatedLabel?.[tamTamyLanguagesMap?.[lang]],
  },
  workPlace: {
    section: 0,
    question: 6,
    answerType: "answerValue",
    box: 0,
    data: (request) => request.workplace,
  },
  phone: {
    section: 0,
    question: 7,
    answerType: "answerValue",
    box: 0,
    data: (request) => request.welfarePhone,
  },
  availability: {
    section: 0,
    question: 8,
    answerType: "answerValue",
    box: 0,
    data: (request) => request.preferredTimeSlot, //TODO
  },
  mail: {
    section: 0,
    question: 9,
    answerType: "answerValue",
    box: 0,
    data: (request) => request.welfareEmail,
  },
  quadro: {
    section: 0,
    question: 12,
    answerType: "label",
    box: 0,
    data: (request, lang, survey) =>
      survey.definition.sections[0].questions[12].answers[request.quadro ? 0 : 1]
        ?.translatedLabel?.[tamTamyLanguagesMap?.[lang]],
  },
  manager: {
    section: 0,
    question: 13,
    answerType: "label",
    box: 0,
    data: (request, lang, survey) =>
      survey.definition.sections[0].questions[13].answers[request.manager ? 0 : 1]
        ?.translatedLabel?.[tamTamyLanguagesMap?.[lang]],
  },
  package: {
    section: 1,
    question: 0,
    answerType: "answerValue",
    behaviour: "package",
    box: 1,
    data: () => undefined,
  },
  optionals: {
    section: 1,
    question: 1,
    data: () => undefined,
  },
  center: {
    section: 2,
    question: 0,
    type: "answerValue",
    behaviour: "center",
    box: 2,
    data: () => undefined,
  },
};

export const getQuestionId = (survey, question) => {
  const q = QUESTION_MAPPING?.[question];
  return survey?.definition?.sections?.[q.section]?.questions?.[q.question]?.id;
};

export const getQuestion = (survey, question) => {
  const q = QUESTION_MAPPING?.[question];
  return survey?.definition?.sections?.[q.section]?.questions?.[q.question];
};

export const getGenderAnswer = (survey, answerValue) => {
  const genderAnswerIndex = getQuestion(survey, "gender")?.answers?.findIndex(
    (ans) => Number(ans.id) === Number(answerValue)
  );
  return genderAnswerIndex === 0 ? "MALE" : "FEMALE";
};

export const getBinaryAnswer = (survey, field, answerValue) => {
  const binaryAnswerIndex = getQuestion(survey, field)?.answers?.findIndex(
    (ans) => Number(ans.id) === Number(answerValue)
  );
  return binaryAnswerIndex === 0 ? true : false;
};

export const getOptionalExamsFromAnswer = (values, optionalExamsQuestionId) => {
  const value = values?.[`${optionalExamsQuestionId}`];
  return value ? (Array.isArray(value) ? value?.map((id) => Number(id)) : [value]) : [];
};

export const getPackagePayloadFromRequest = (request) => {
  return {
    optionalsExams: request?.packageSelected?.optionals?.map((exam) => exam.id),
    package: request?.packageSelected?.pack?.id,
  };
};

export const computeCheckUpPartial = (survey, submission) => {
  const optionalExamsQuestionId = getQuestionId(survey, "optionals");
  const optionalExamsAnswer = submission?.[optionalExamsQuestionId]?.map((id) => Number(id));
  return computePartial(survey, {
    ...submission,
    ...(optionalExamsAnswer && optionalExamsAnswer?.length > 0
      ? { [`${optionalExamsQuestionId}`]: JSON.stringify(optionalExamsAnswer) }
      : {}),
  });
};

export const parseCheckupSubmission = (survey, submission) => {
  if (survey && submission) {
    const optionalExamsQuestionId = getQuestionId(survey, "optionals");
    const parsedSubmission = parseSubmission(survey, submission);
    const optionalExamsStringAnswer = parsedSubmission?.[`${optionalExamsQuestionId}`];
    if (optionalExamsStringAnswer) {
      parsedSubmission[`${optionalExamsQuestionId}`] = JSON.parse(optionalExamsStringAnswer)?.map(
        (id) => id.toString()
      );
    }
    return parsedSubmission;
  }
  return;
};

export const getNotProvidedExams = (request, clinic) => {
  const packageExams = request?.packageSelected?.pack?.exams?.map((cat) => cat?.exams).flat();
  return packageExams
    ?.concat(request?.packageSelected?.optionals)
    ?.filter((exam) => !clinic?.exams?.map((e) => e.id)?.includes(exam?.id));
};

export const parseRequest = (request) => {
  const categories = request?.packageSelected?.pack?.exams
    ?.map((exam) => exam.category)
    ?.filter((item, i, self) => self.indexOf(item) === i);

  return {
    ...request,
    ...(request.status === "CANCELED"
      ? { lastCompletedStep: 2 }
      : { lastCompletedStep: request?.lastCompletedStep }),
    ...(request.examFile
      ? {
          examFile: {
            ...request.examFile,
            ...getFilenameDetails(request.examFile.fileName),
          },
        }
      : {}),
    ...(request.packageSelected
      ? {
          packageSelected: {
            ...request.packageSelected,
            pack: {
              ...request.packageSelected.pack,
              exams: categories?.map((cat) => ({
                category: cat,
                exams: request.packageSelected.pack?.exams?.filter((exam) => exam.category === cat),
              })),
              optionals: request.packageSelected.pack.optionals?.sort((a, b) =>
                a.name.toUpperCase() > b.name.toUpperCase()
                  ? 1
                  : a.name.toUpperCase() < b.name.toUpperCase()
                  ? -1
                  : 0
              ),
            },
          },
        }
      : {}),
  };
};
